import { Form, Input, InputNumber, Select, Space } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import { RevxSpin } from 'components/RevxSpin';
import { FilterOperatorEnum } from 'domains/appDomain';
import { EqptFilterObj } from 'domains/eqptDomain';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  normalizeFilterObj,
  normalizeToFormFields,
  normalizeValues,
} from 'utils/filter';
import { FilterOperatorSelect } from '../common/FilterOperatorSelect';
import { FormItemForFilterValues } from '../common/FormItemForFilterValues';
import {
  isSingleOperator,
  MARKUP_OPERATORS,
  NUMERIC_OPERATORS,
} from '../common/utils';

// the filter form is used both on the equipment list screen and inside the
// "create equipment from base" drawer, each one keeping its own filter state
export type EqptFilterScope = 'main' | 'baseSelect';

type Props = {
  scope?: EqptFilterScope;
};

export const EqptFilterForm = ({ scope = 'main' }: Props) => {
  const [form] = useForm();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const isBaseSelect = scope === 'baseSelect';
  const filterObj = useAppSelector((state) =>
    isBaseSelect ? state.eqpt.eqptBaseSelect.filterObj : state.eqpt.filterObj
  );
  const loading = useAppSelector((state) =>
    isBaseSelect
      ? state.loading.effects.eqpt.getListForBaseSelect
      : state.loading.effects.eqpt.getList
  );
  const [markupOperator, setMarkupOperator] = useState(
    filterObj.markupPct?.operator
  );

  const onFinish = (values: EqptFilterObj) => {
    const filterObj = normalizeFilterObj(values);
    if (isBaseSelect) {
      dispatch.eqpt.saveBaseSelectFilter(filterObj);
    } else {
      dispatch.eqpt.saveFilter(filterObj);
    }
  };

  useEffect(() => {
    form.setFieldsValue(normalizeToFormFields(filterObj));
    setMarkupOperator(filterObj.markupPct?.operator);
  }, [filterObj, form]);

  const clearFilters = () => {
    if (isBaseSelect) {
      dispatch.eqpt.removeBaseSelectFilter();
    } else {
      dispatch.eqpt.removeFilter();
    }
    form.resetFields();
  };
  const eqptTypes = useAppSelector((state) => state.eqpt.eqptTypes);

  return (
    <RevxSpin spinning={loading}>
      <Form
        layout="vertical"
        form={form}
        onFinish={onFinish}
        autoComplete="off"
      >
        <Form.Item label={t('platform.filter.formLabel.revxId')}>
          <Form.Item
            noStyle
            name={['revxId', 'operator']}
            initialValue={FilterOperatorEnum.EQUALS}
          >
            <Input hidden />
          </Form.Item>
          <FormItemForFilterValues noStyle name={['revxId', 'values']}>
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              maxLength={15}
              max={Number.MAX_SAFE_INTEGER}
              controls={false}
            />
          </FormItemForFilterValues>
        </Form.Item>
        <Form.Item label={t('platform.filter.formLabel.eqptType')}>
          <Form.Item
            noStyle
            name={['typeId', 'operator']}
            initialValue={FilterOperatorEnum.IN}
          >
            <Input hidden />
          </Form.Item>

          <FormItemForFilterValues noStyle name={['typeId', 'values']}>
            <Select
              mode="multiple"
              showSearch
              filterOption={(value, option) => {
                return String(option?.children)
                  .toLocaleLowerCase()
                  .includes(value.toLocaleLowerCase());
              }}
              getPopupContainer={(e) => {
                return e as HTMLElement;
              }}
            >
              {eqptTypes.map((t) => {
                return (
                  <Select.Option key={t.code} value={t.id}>
                    {t.name}
                  </Select.Option>
                );
              })}
            </Select>
          </FormItemForFilterValues>
        </Form.Item>
        <Form.Item label={t('platform.filter.formLabel.manufacturerContains')}>
          <Form.Item
            noStyle
            name={['manufacturer', 'operator']}
            initialValue={FilterOperatorEnum.CONTAINS}
          >
            <Input hidden />
          </Form.Item>
          <FormItemForFilterValues noStyle name={['manufacturer', 'values']}>
            <Input />
          </FormItemForFilterValues>
        </Form.Item>
        <Form.Item label={t('platform.filter.formLabel.name')}>
          <Form.Item
            noStyle
            name={['name', 'operator']}
            initialValue={FilterOperatorEnum.CONTAINS}
          >
            <Input hidden />
          </Form.Item>
          <FormItemForFilterValues noStyle name={['name', 'values']}>
            <Input />
          </FormItemForFilterValues>
        </Form.Item>

        <Form.Item
          label={t('platform.filter.formLabel.avgRunSpeedSmall')}
          name={['avgRunSpeedSmall', 'values']}
          normalize={normalizeValues}
        >
          <InputNumber
            addonBefore={
              <Form.Item
                name={['avgRunSpeedSmall', 'operator']}
                noStyle
                initialValue={FilterOperatorEnum.EQUALS}
              >
                <FilterOperatorSelect operatorOptions={NUMERIC_OPERATORS} />
              </Form.Item>
            }
          />
        </Form.Item>

        <Form.Item
          label={t('platform.filter.formLabel.avgRunSpeedLarge')}
          name={['avgRunSpeedLarge', 'values']}
          normalize={normalizeValues}
        >
          <InputNumber
            addonBefore={
              <Form.Item
                name={['avgRunSpeedLarge', 'operator']}
                noStyle
                initialValue={FilterOperatorEnum.EQUALS}
              >
                <FilterOperatorSelect operatorOptions={NUMERIC_OPERATORS} />
              </Form.Item>
            }
          />
        </Form.Item>
        <Form.Item
          label={t('platform.filter.formLabel.hourlyIncrement')}
          name={['hourlyIncrement', 'values']}
          normalize={normalizeValues}
        >
          <InputNumber
            addonBefore={
              <Form.Item
                name={['hourlyIncrement', 'operator']}
                noStyle
                initialValue={FilterOperatorEnum.EQUALS}
              >
                <FilterOperatorSelect operatorOptions={NUMERIC_OPERATORS} />
              </Form.Item>
            }
          />
        </Form.Item>
        <Form.Item
          label={t('platform.filter.formLabel.printUnits')}
          name={['printUnits', 'values']}
          normalize={normalizeValues}
        >
          <InputNumber
            addonBefore={
              <Form.Item
                name={['printUnits', 'operator']}
                noStyle
                initialValue={FilterOperatorEnum.EQUALS}
              >
                <FilterOperatorSelect operatorOptions={NUMERIC_OPERATORS} />
              </Form.Item>
            }
          />
        </Form.Item>
        <Form.Item label={t('platform.filter.formLabel.perfectorConfig')}>
          <Form.Item
            noStyle
            name={['perfectorConfig', 'operator']}
            initialValue={FilterOperatorEnum.CONTAINS}
          >
            <Input hidden />
          </Form.Item>
          <FormItemForFilterValues noStyle name={['perfectorConfig', 'values']}>
            <Input />
          </FormItemForFilterValues>
        </Form.Item>
        <Form.Item
          label={t('platform.filter.formLabel.coatingUnit')}
          name={['coatingUnit', 'values']}
          normalize={normalizeValues}
        >
          <InputNumber
            addonBefore={
              <Form.Item
                name={['coatingUnit', 'operator']}
                noStyle
                initialValue={FilterOperatorEnum.EQUALS}
              >
                <FilterOperatorSelect operatorOptions={NUMERIC_OPERATORS} />
              </Form.Item>
            }
          />
        </Form.Item>

        <Form.Item
          label={t('platform.filter.formLabel.markupPCT')}
          name={['markupPct', 'values']}
          normalize={normalizeValues}
        >
          <InputNumber
            disabled={markupOperator && isSingleOperator(markupOperator)}
            addonBefore={
              <Form.Item
                name={['markupPct', 'operator']}
                noStyle
                initialValue={FilterOperatorEnum.EQUALS}
              >
                <FilterOperatorSelect
                  operatorOptions={MARKUP_OPERATORS}
                  onChange={(val) => {
                    setMarkupOperator(val as FilterOperatorEnum);

                    if (isSingleOperator(val as FilterOperatorEnum)) {
                      form.setFieldsValue({
                        markupPct: {
                          values: [],
                          operator: val,
                        },
                      });
                    }
                  }}
                />
              </Form.Item>
            }
          />
        </Form.Item>

        <Form.Item>
          <Space>
            <RevxButton
              type="primary"
              htmlType="submit"
              brandLightPurple
              className={
                isBaseSelect ? 'base-select-filter-btn' : 'admin-filter-btn'
              }
            >
              {t('platform.filter.filterBtnLabel')}
            </RevxButton>
            <RevxButton
              className={
                isBaseSelect
                  ? 'base-select-clear-filter-btn'
                  : 'admin-clear-filter-btn'
              }
              onClick={() => {
                clearFilters();
              }}
              brandGreen
            >
              {t('platform.filter.clearFilterBtnLabel')}
            </RevxButton>
          </Space>
        </Form.Item>
      </Form>
    </RevxSpin>
  );
};
